import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  getEffectiveFormQuestions,
  isLeadCaptureTracker,
} from "./public/publicJoinStorage";
import { submitMemberTrackerApi } from "src/DAL/tracker/tracker";
import PublicTrackerField, {
  usePublicFormState,
} from "./public/PublicTrackerField";
import { getJourneySteps } from "./memberTrackersStorage";
import {
  buildGuidedSteps,
  buildIdentitySeedAnswers,
  filterMemberVisibleQuestions,
  filterPublicQuestions,
  getLoggedInMemberPrefill,
} from "./public/publicTrackerFlow";

const MUTED = "#64748b";
const INK = "#0f172a";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";
const LINE = "#e8edf2";

/**
 * Portal check-in — single page, sectioned fields, flat layout.
 */
export default function MemberTrackerForm({ tracker, onCancel, onSuccess }) {
  const memberInfo = useMemo(() => getLoggedInMemberPrefill(), []);
  const isLead = isLeadCaptureTracker(tracker);

  const memberSubs = useMemo(
    () =>
      (tracker?._memberSubmissions || []).map((row) => ({
        ...row,
        id: String(row._id || row.id),
        formId: row.formId,
        submittedAt: row.submittedAt,
      })),
    [tracker]
  );

  const journeySteps = useMemo(
    () => getJourneySteps(tracker, memberSubs),
    [tracker, memberSubs]
  );

  const form = useMemo(() => {
    const current = journeySteps.find((s) => s.state === "current");
    if (current?.id) {
      return (tracker.forms || []).find((f) => f.id === current.id) || null;
    }
    return (
      (tracker.forms || []).find((f) => f.role === "first_submission") ||
      (tracker.forms || [])[0] ||
      null
    );
  }, [journeySteps, tracker]);

  const allQuestions = useMemo(() => {
    if (!tracker || !form) return [];
    return getEffectiveFormQuestions(
      form,
      tracker.sharedQuestions || [],
      tracker.personalInfoQuestions || [],
      tracker.forms || []
    );
  }, [tracker, form]);

  const visibleQuestions = useMemo(
    () => filterMemberVisibleQuestions(allQuestions),
    [allQuestions]
  );

  const identitySeed = useMemo(
    () =>
      buildIdentitySeedAnswers(filterPublicQuestions(allQuestions), memberInfo),
    [allQuestions, memberInfo]
  );

  const { answers, setAnswer, errors, setErrors } = usePublicFormState(
    visibleQuestions,
    identitySeed
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const sections = useMemo(() => {
    const guided = buildGuidedSteps(visibleQuestions, {
      isLead,
      forceReview: false,
    });
    return guided.filter((s) => !s.isReview && (s.questions || []).length);
  }, [visibleQuestions, isLead]);

  const title = form?.name || tracker?.name || "Check-in";

  const validateAll = () => {
    const nextErrors = {};
    visibleQuestions.forEach((q) => {
      const value = answers[q.id];
      const emptyArray = Array.isArray(value) && value.length === 0;
      const empty = value == null || value === "" || emptyArray;
      if (q?.required && empty) nextErrors[q.id] = "Required";
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const firstId = Object.keys(nextErrors)[0];
      document
        .getElementById(`member-q-${firstId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  };

  const finishSubmit = async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      const idOrSlug = tracker.id || tracker._id || tracker.publicSlug;
      const res = await submitMemberTrackerApi(idOrSlug, {
        formId: form?.id || "",
        answers: { ...identitySeed, ...answers },
      });
      if (res?.code === 200) {
        onSuccess?.(res);
        return;
      }
      if (res?.errors && typeof res.errors === "object") {
        setErrors((prev) => ({ ...prev, ...res.errors }));
      }
      setSubmitError(res?.message || "Submission failed. Please try again.");
    } catch (_e) {
      setSubmitError("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateAll()) return;
    finishSubmit();
  };

  if (!visibleQuestions.length) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography sx={{ fontWeight: 600, color: INK, mb: 1, fontSize: 18 }}>
          Nothing to fill in
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: 14, mb: 2 }}>
          This tracker has no member questions yet.
        </Typography>
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={onCancel}
        >
          Back
        </button>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{ width: "100%" }}
    >
      <h2 style={{ margin: "0 0 6px" }}>{title}</h2>
      <Typography sx={{ fontSize: 14, color: MUTED, lineHeight: 1.5, mb: 3.5 }}>
        Your coach will see these answers on your profile.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {sections.map((section, i) => (
          <Box
            key={section.id}
            sx={{
              py: 3,
              borderTop: i === 0 ? "none" : `1px solid ${LINE}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: MUTED,
                mb: 2,
              }}
            >
              {section.short || "Questions"}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
              {(section.questions || []).map((question) => (
                <Box key={question.id} id={`member-q-${question.id}`}>
                  <Typography
                    component="label"
                    htmlFor={`member-input-${question.id}`}
                    sx={{
                      display: "block",
                      fontWeight: 500,
                      fontSize: 14.5,
                      color: INK,
                      mb: 1,
                      lineHeight: 1.4,
                    }}
                  >
                    {question.label || question.statement || "Question"}
                    {question.required ? (
                      <Box component="span" sx={{ color: PRIMARY, ml: 0.35 }}>
                        *
                      </Box>
                    ) : null}
                  </Typography>
                  <PublicTrackerField
                    question={question}
                    value={answers[question.id]}
                    error={errors[question.id]}
                    onChange={(value) => setAnswer(question.id, value)}
                    surveyMode
                    hideLabel
                    portalFlat
                    inputId={`member-input-${question.id}`}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      {submitError ? (
        <Typography sx={{ mt: 1, mb: 2, fontSize: 13.5, color: "#dc2626" }}>
          {submitError}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.25,
          pt: 2.5,
          mt: 1,
          borderTop: `1px solid ${LINE}`,
          pb: 2,
        }}
      >
        <button
          type="button"
          className="small-contained-outlined-button"
          disabled={submitting}
          onClick={onCancel}
          style={{ opacity: submitting ? 0.4 : 1, minWidth: 100 }}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="small-contained-button"
          disabled={submitting}
          style={{ opacity: submitting ? 0.55 : 1, minWidth: 120 }}
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </Box>
    </Box>
  );
}
